import { useQuery } from '@tanstack/react-query'
import { getRegistryStatus } from '@/services'
import { AppButton, HealthDot, Skeleton } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'

type InfraStatus = 'ok' | 'degraded' | 'down' | 'unknown'

function InfraCard({ name, icon, status, url, meta, placeholder, action, onAction }: {
  name: string
  icon: string
  status: InfraStatus
  url: string
  meta: string
  placeholder?: boolean
  action?: string
  onAction?: () => void
}) {
  const tone = status === 'ok' ? 'success' : status === 'degraded' ? 'warning' : status === 'down' ? 'danger' : 'neutral'
  return (
    <div className="card">
      <div style={{ padding: 18 }}>
        <div className="row gap-3" style={{ alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: `var(--${tone}-bg)`, color: `var(--${tone}-fg)`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name={icon} size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 15, letterSpacing: '-0.005em' }}>{name}</div>
            <div className="muted" style={{ fontFamily: 'var(--font-mono)', fontSize: 11, marginTop: 2 }}>{url}</div>
          </div>
          <HealthDot status={status} />
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 12 }}>{meta}</div>
        <div className="row gap-2">
          {action ? <AppButton variant="secondary" size="sm" onClick={onAction}>{action}</AppButton> : null}
          {placeholder ? <span className="badge badge-outline badge-sm" style={{ color: 'var(--text-3)' }}>Health check próximamente</span> : null}
        </div>
      </div>
    </div>
  )
}

function InfraCardSkeleton() {
  return (
    <div className="card">
      <div style={{ padding: 18 }}>
        <div className="row gap-3" style={{ alignItems: 'flex-start', marginBottom: 12 }}>
          <Skeleton width={36} height={36} radius={8} />
          <div style={{ flex: 1 }}>
            <Skeleton width="55%" height={16} style={{ marginBottom: 8 }} />
            <Skeleton width="80%" height={12} />
          </div>
          <Skeleton width={70} height={22} radius={999} />
        </div>
        <Skeleton width="70%" height={14} style={{ marginBottom: 12 }} />
        <Skeleton width={130} height={24} radius={999} />
      </div>
    </div>
  )
}

export function InfraPage() {
  const { data: registry, isLoading } = useQuery({ queryKey: ['registry-status'], queryFn: getRegistryStatus, retry: false })

  const registryConfigured = registry?.configured
  const registryUrl = registry?.registry?.url || 'registry.bisne.app'

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Plataforma</div>
          <h1>Infra</h1>
          <div className="subtitle">Estado de la infraestructura compartida. Más checks llegan pronto.</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="refresh">Re-chequear todo</AppButton>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {isLoading ? <InfraCardSkeleton /> : <InfraCard
            name="Docker Registry"
            icon="package"
            status={registryConfigured ? 'ok' : 'unknown'}
            url={registryUrl}
            meta={registryConfigured ? `Configurado${registry?.registry?.username ? ` · ${registry.registry.username}` : ''}` : 'Sin configurar'}
            action={registryConfigured ? undefined : 'Setup'}
          />}
        <InfraCard name="Traefik (proxy)" icon="globe" status="ok" url="traefik.bisne.app" meta="Reverse proxy + SSL" placeholder />
        <InfraCard name="MinIO (storage)" icon="hardDrive" status="ok" url="s3.bisne.app" meta="Object storage compartido" placeholder />
        <InfraCard name="Redis" icon="zap" status="ok" url="redis.bisne.app:6379" meta="Cache + colas" placeholder />
        <InfraCard name="PostgreSQL (platform DB)" icon="server" status="ok" url="db.bisne.app:5432" meta="Base de datos de plataforma" placeholder />
        <InfraCard name="Backups (S3 + Restic)" icon="cloud" status="ok" url="backups.bisne.app" meta="Retención 30d" placeholder />
      </div>
    </>
  )
}
